/**
 * FunnelCard Component
 * Reusable conversion funnel visualization with progress bars
 *
 * @description Shared funnel chart for analytics dashboards (admin and provider)
 */

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../ui/card";
import { cn } from "../ui/cn";

interface FunnelStage {
  readonly stage: string;
  readonly value: number;
  readonly conversion_rate: number;
}

interface FunnelCardProps {
  readonly data: FunnelStage[] | null;
  readonly title: string;
  readonly description?: string;
  readonly colorMap?: Record<string, string>;
  readonly footerText?: string;
  /** Optional className for card container (e.g., h-full for grid layouts) */
  readonly className?: string;
}

const DEFAULT_COLOR_MAP: Record<string, string> = {
  "Home Visits": "hsl(43, 87%, 60%)", // #F3B33B
  "Title Views": "hsl(142, 76%, 45%)", // #10B981
  "Watch Starts": "hsl(217, 91%, 60%)", // #3B82F6
  Completions: "hsl(258, 90%, 66%)", // #8B5CF6
};

export function FunnelCard({
  data,
  title,
  description,
  colorMap = DEFAULT_COLOR_MAP,
  footerText = "Tracking user progression from landing to completion",
  className,
}: FunnelCardProps) {
  // Show loading state
  if (!data) {
    return (
      <Card className={cn("border-neutral-800 bg-neutral-900/50 flex flex-col", className)}>
        <CardHeader>
          <CardTitle className="text-white">{title}</CardTitle>
          {description && <CardDescription className="text-neutral-400">{description}</CardDescription>}
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex items-center justify-center h-[240px] text-neutral-500">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  // Show empty state
  if (data.length === 0) {
    return (
      <Card className={cn("border-neutral-800 bg-neutral-900/50 flex flex-col", className)}>
        <CardHeader>
          <CardTitle className="text-white">{title}</CardTitle>
          {description && <CardDescription className="text-neutral-400">{description}</CardDescription>}
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex items-center justify-center h-[240px] text-neutral-500">No data available</div>
        </CardContent>
      </Card>
    );
  }

  const topStageValue = data[0]?.value || 1;

  return (
    <Card
      className={cn(
        "border-neutral-800 bg-neutral-900/50 hover:bg-neutral-900/70 transition-colors flex flex-col",
        className,
      )}
    >
      <CardHeader>
        <CardTitle className="text-white">{title}</CardTitle>
        {description && <CardDescription className="text-neutral-400">{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {data.map((stage) => (
            <div key={stage.stage} className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-white">{stage.stage}</span>
                <span className="text-neutral-400">
                  {stage.value.toLocaleString()} ({stage.conversion_rate}%)
                </span>
              </div>
              <div className="h-3 bg-neutral-800 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{
                    width: `${(stage.value / topStageValue) * 100}%`,
                    backgroundColor: colorMap[stage.stage] || "hsl(var(--muted))",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
      <CardFooter>
        <div className="flex w-full items-start gap-2 text-sm">
          <div className="grid gap-2">
            <div className="flex items-center gap-2 leading-none text-neutral-500">{footerText}</div>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
}
